import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { API_URL } from '../../services/api';

/**
 * Editeur des pierres d'un devis (recherche stock + saisie poids/qte/prix).
 * Source UNIQUE partagee par CreateDevis et EditDevisModal.
 *
 * Props :
 *   - pierres : tableau de pierres
 *       { reference, type, poids_carats, quantite, stock_id,
 *         prix_achat_unitaire, prix_a_verifier, designation, stock_disponible }
 *   - onChange(newPierres) : remonte le nouveau tableau au parent
 *
 * NB : le prix au carat = prix_achat_unitaire (prix_achat_carat est NULL en base).
 *      Prix <= 0 (regul inventaire) : pas d'auto-remplissage + saisie manuelle.
 */
export default function PierresEditor({ pierres, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(-1);

  const update = (idx, patch) => {
    onChange(pierres.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addPierre = () => {
    onChange([...pierres, { reference: '', type: '', poids_carats: '', quantite: 1, marge_individuelle: 0 }]);
  };

  const removePierre = (idx) => {
    onChange(pierres.filter((_, i) => i !== idx));
  };

  const searchPierres = async (query, index) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(-1);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/stock-matieres/search-pierres?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSuggestions(Array.isArray(data) ? data : []);
      setShowSuggestions(index);
    } catch (error) {
      console.error('Erreur recherche pierres:', error);
      setSuggestions([]);
    }
  };

  const selectPierre = (pierre, index) => {
    // prix_achat_unitaire = prix au carat (prix_achat_carat est NULL en base)
    const prixCarat = parseFloat(pierre.prix_achat_unitaire || 0);
    const prixValide = prixCarat > 0;
    if (!prixValide) {
      alert(`⚠️ Prix à vérifier pour ${pierre.reference} : prix au carat absent ou négatif en stock (régul inventaire). Saisissez-le manuellement.`);
    }
    onChange(pierres.map((p, i) => (i === index ? {
      reference: pierre.reference,
      type: pierre.type_matiere,
      poids_carats: '', // L'utilisateur saisira le poids dont il a besoin
      quantite: 1,
      stock_id: pierre.id,
      prix_achat_unitaire: prixValide ? prixCarat : '', // pas d'auto-remplissage si <= 0
      prix_a_verifier: !prixValide,
      designation: pierre.designation,
      stock_disponible: parseFloat(pierre.quantite_disponible || 0),
    } : p)));
    setSuggestions([]);
    setShowSuggestions(-1);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h4 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="text-2xl">💎</span> Pierres Précieuses
          </h4>
          <p className="text-sm text-gray-500 mt-1">Ajoutez les diamants et pierres précieuses</p>
        </div>
        <button
          onClick={addPierre}
          className="px-5 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-semibold flex items-center space-x-2 hover:from-purple-600 hover:to-purple-700 shadow-lg transition-all"
        >
          <Plus size={20} />
          <span>Ajouter une Pierre</span>
        </button>
      </div>
      {pierres.map((p, idx) => (
        <div key={idx} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 mb-4 border-2 border-purple-300 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💎</span>
              <div>
                <div className="font-bold text-lg text-gray-800">{p.reference || 'Nouvelle Pierre'}</div>
                <div className="text-sm text-gray-600">{p.type || 'Type non défini'}</div>
              </div>
            </div>
            <button
              onClick={() => removePierre(idx)}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 transition-all"
            >
              <Trash2 size={18} />
              <span>Supprimer</span>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-4">
            <div className="relative col-span-full">
              <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Référence Fournisseur</label>
              <input
                type="text"
                placeholder="Tapez DIA140, COCO..."
                value={p.reference}
                onChange={(e) => {
                  update(idx, { reference: e.target.value });
                  searchPierres(e.target.value, idx);
                }}
                onFocus={() => {
                  if (p.reference && p.reference.length >= 1) {
                    searchPierres(p.reference, idx);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSuggestions(-1);
                    setSuggestions([]);
                  }, 300);
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base"
              />

              {/* Suggestions autocomplete */}
              {showSuggestions === idx && suggestions.length > 0 && (
                <div className="absolute z-[9999] w-full mt-1 bg-white border-2 border-blue-400 rounded-xl shadow-2xl max-h-96 overflow-y-auto" style={{ position: 'absolute', zIndex: 9999 }}>
                  {suggestions.map((pierre, pIdx) => (
                    <div
                      key={pIdx}
                      onClick={() => selectPierre(pierre, idx)}
                      className="px-5 py-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 cursor-pointer border-b last:border-b-0 transition-all duration-200"
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-bold text-base text-blue-700">{pierre.reference}</div>
                        <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                          {parseFloat(pierre.prix_achat_unitaire || 0) > 0
                            ? `${parseFloat(pierre.prix_achat_unitaire).toFixed(2)} DH/carat`
                            : '⚠️ prix à vérifier'}
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 mb-2 font-medium">
                        {pierre.designation} • {pierre.type_matiere}
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="bg-purple-50 text-purple-700 px-3 py-1 rounded-lg text-sm font-semibold">
                          💎 {parseFloat(pierre.quantite_disponible || 0).toFixed(3)} carats
                        </div>
                        {pierre.numero_certificat && (
                          <div className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-lg text-xs font-medium">
                            📜 Cert: {pierre.numero_certificat}
                          </div>
                        )}
                        {pierre.fournisseur_nom && (
                          <div className="text-xs text-gray-500">
                            Fournisseur: {pierre.fournisseur_nom}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">🏷️ Type de Pierre</label>
              <input
                type="text"
                placeholder="DIAMANT, SAPHIR..."
                value={p.type}
                onChange={(e) => update(idx, { type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {/* Nombre de pierres */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">🔢 Nombre de pierres</label>
              <input
                type="text" inputMode="decimal" onFocus={e => e.target.select()}
                step="1"
                min="1"
                placeholder="1"
                value={p.quantite}
                onChange={(e) => update(idx, { quantite: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-base font-semibold"
              />
              <p className="text-xs text-gray-500 mt-1">Ex: 2 diamants identiques</p>
            </div>

            {/* Poids par pierre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">💎 Poids par pierre (ct)</label>
              <div className="relative">
                <input
                  type="text" inputMode="decimal" onFocus={e => e.target.select()}
                  step="0.001"
                  placeholder="0.000"
                  value={p.poids_carats}
                  onChange={(e) => update(idx, { poids_carats: e.target.value })}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 text-base font-semibold ${
                    p.stock_disponible !== undefined && (p.poids_carats * (p.quantite || 1)) > p.stock_disponible
                      ? 'border-red-500 bg-red-50 focus:border-red-600 focus:ring-red-200'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">ct</span>
              </div>
              {/* Total carats */}
              {(p.quantite > 1 && p.poids_carats > 0) && (
                <p className="text-xs text-purple-700 font-semibold mt-1">
                  Total : {(p.poids_carats * p.quantite).toFixed(4)} ct ({p.quantite} × {p.poids_carats} ct)
                </p>
              )}
              {/* Alerte stock */}
              {p.stock_disponible !== undefined && (
                <div className={`text-xs font-semibold p-1.5 rounded mt-1 ${
                  (p.poids_carats * (p.quantite || 1)) > p.stock_disponible
                    ? 'bg-red-100 text-red-700'
                    : p.stock_disponible === 0
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {(p.poids_carats * (p.quantite || 1)) > p.stock_disponible
                    ? `⚠️ Stock insuff. (${p.stock_disponible.toFixed(3)} ct dispo)`
                    : p.stock_disponible === 0
                    ? '⚠️ Stock vide'
                    : `✓ Stock: ${p.stock_disponible.toFixed(3)} ct`}
                </div>
              )}
            </div>

            {/* Prix au carat (editable - saisie manuelle possible) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">💰 Prix au carat (DH)</label>
              <input
                type="text" inputMode="decimal" onFocus={e => e.target.select()}
                placeholder="0.00"
                value={p.prix_achat_unitaire}
                onChange={(e) => update(idx, { prix_achat_unitaire: e.target.value, prix_a_verifier: false })}
                className={`w-full px-4 py-3 border-2 rounded-lg text-base font-bold ${
                  parseFloat(p.prix_achat_unitaire || 0) > 0
                    ? 'border-green-300 bg-green-50 text-green-700 focus:border-green-500 focus:ring-green-200'
                    : 'border-orange-400 bg-orange-50 text-orange-700 focus:border-orange-500 focus:ring-orange-200'
                }`}
              />
              {parseFloat(p.prix_achat_unitaire || 0) <= 0 && (
                <p className="text-xs text-orange-600 font-semibold mt-1">⚠️ Prix à vérifier / à saisir</p>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">💵 Prix Total de cette ligne :</span>
              <span className="text-2xl font-bold text-purple-700">
                {(p.poids_carats * (p.prix_achat_unitaire || 0) * (p.quantite || 1)).toFixed(2)} DH
              </span>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {p.quantite > 1
                ? `${p.quantite} pierres × ${p.poids_carats || 0} ct/pce × ${parseFloat(p.prix_achat_unitaire || 0).toFixed(2)} DH/ct = ${(p.poids_carats * (p.prix_achat_unitaire || 0) * (p.quantite || 1)).toFixed(2)} DH`
                : `${p.poids_carats || 0} ct × ${parseFloat(p.prix_achat_unitaire || 0).toFixed(2)} DH/ct = ${(p.poids_carats * (p.prix_achat_unitaire || 0)).toFixed(2)} DH`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
